import { useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import {
  HardDrive,
  RefreshCw,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Zap,
  Shield,
} from "lucide-react";
import { DiskService } from "../../../../application/services/diskService";
import { useDiskAnalysis } from "../../../hooks/aiHooks/useDiskAnalysis";

const diskService = new DiskService();

const DiskSpaceSummary = ({ vmIds }) => {
  const [diskData, setDiskData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {
    getDiskAnalysis,
    diskAnalysis,
    loading: aiLoading,
  } = useDiskAnalysis();

  useEffect(() => {
    if (vmIds && vmIds.length > 0) {
      fetchDiskData();
    }
  }, [vmIds]);

  const fetchDiskData = async () => {
    setLoading(true);
    setError(null);
    try {
      const results = [];
      for (const vmId of vmIds) {
        const result = await diskService.getVmDiskInfo(vmId);
        if (
          result.success &&
          result.data &&
          result.data.diskFreePercent !== null
        ) {
          const analysisResult = await getDiskAnalysis(vmId);
          results.push({
            vmId: vmId,
            vmName: result.data.vmName,
            workspaceName: result.data.workspaceName,
            freePercent: result.data.diskFreePercent,
            freeMB: result.data.diskFreeMB,
            freeGB: result.data.diskFreeGB,
            lastUpdated: result.data.diskLastUpdated,
            aiAnalysis: analysisResult.success ? analysisResult.data : null,
          });
        }
      }
      setDiskData(results);
    } catch (err) {
      console.error("Error fetching disk data:", err);
      setError("Failed to load disk space data");
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilFullDisplay = (days) => {
    if (!days || days === 0) return "No data";
    if (days > 365) return `more than a year`;
    if (days > 30) return `${Math.floor(days / 30)} months (${days} days)`;
    return `${days} days`;
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toUpperCase()) {
      case "CRITICAL":
        return "text-red-600 bg-red-500/10 border-red-500/30";
      case "HIGH":
        return "text-orange-500 bg-orange-500/10 border-orange-500/30";
      case "MEDIUM":
        return "text-yellow-500 bg-yellow-500/10 border-yellow-500/30";
      case "LOW":
        return "text-green-500 bg-green-500/10 border-green-500/30";
      default:
        return "text-gray-400 bg-gray-500/10 border-gray-500/30";
    }
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel?.toUpperCase()) {
      case "CRITICAL":
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case "HIGH":
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case "MEDIUM":
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case "LOW":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <HardDrive className="w-5 h-5 text-gray-400" />;
    }
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 sm:p-3 shadow-lg">
          <p className="text-gray-300 text-xs sm:text-sm mb-1">{dataPoint.name}</p>
          <p className="text-primary-cyan text-base sm:text-lg font-bold">
            {dataPoint.value.toFixed(1)}% Free
          </p>
          <p className="text-gray-400 text-[10px] sm:text-xs">
            {dataPoint.freeGB} GB / {dataPoint.freeMB} MB free
          </p>
        </div>
      );
    }
    return null;
  };

  if (!vmIds || vmIds.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 mx-2 sm:m-4">
        <div className="flex items-center justify-between mb-4 sm:mb-6 pb-3 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <HardDrive className="text-primary-cyan text-xl sm:text-2xl" />
            <h3 className="text-lg sm:text-xl font-semibold text-white">Disk Space Summary</h3>
          </div>
          <RefreshCw className="text-gray-400 animate-spin" />
        </div>
        <div className="text-center py-6 sm:py-8">
          <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-3 text-sm sm:text-base">Loading disk space data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 mx-2 sm:m-4">
        <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
          <HardDrive className="text-primary-cyan text-xl sm:text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">Disk Space Summary</h3>
        </div>
        <div className="text-center py-6 sm:py-8">
          <p className="text-red-400 text-sm sm:text-base">{error}</p>
        </div>
      </div>
    );
  }

  if (diskData.length === 0) {
    return (
      <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 mx-2 sm:m-4">
        <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
          <HardDrive className="text-primary-cyan text-xl sm:text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">Disk Space Summary</h3>
        </div>
        <div className="text-center py-6 sm:py-8">
          <p className="text-gray-400 text-sm sm:text-base">No disk space data available for selected VMs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 mx-2 sm:m-4">
      <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
        <HardDrive className="text-primary-cyan text-xl sm:text-2xl" />
        <h3 className="text-lg sm:text-xl font-semibold text-white">Disk Space Summary</h3>
      </div>

      <div className="space-y-6 sm:space-y-8">
        {diskData.map((vm) => {
          const pieData = [
            {
              name: "Free Space",
              value: vm.freePercent,
              freeGB: vm.freeGB,
              freeMB: vm.freeMB,
            },
            {
              name: "Used Space",
              value: 100 - vm.freePercent,
              freeGB: 0,
              freeMB: 0,
            },
          ];

          const analysis = vm.aiAnalysis;

          return (
            <div
              key={vm.vmId}
              className="bg-primary-dark rounded-xl p-4 sm:p-5 border border-gray-700 transition-all"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-white break-words">
                    {vm.vmName}
                  </h4>
                  <span className="text-[10px] sm:text-xs text-gray-400">
                    Workspace: {vm.workspaceName || "N/A"}
                  </span>
                </div>
                <div className="text-left sm:text-right">
                  <span className="text-[10px] sm:text-xs text-gray-400">Last updated</span>
                  <p className="text-xs sm:text-sm text-gray-300">
                    {vm.lastUpdated ? new Date(vm.lastUpdated).toLocaleString() : "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
                <div className="w-full lg:w-1/2">
                  <div className="h-[200px] sm:h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={60}
                          paddingAngle={2}
                          dataKey="value"
                          animationDuration={500}
                          label={({ value }) => `${value.toFixed(0)}%`}
                          labelLine={{ stroke: "#9CA3AF", strokeWidth: 1 }}
                        >
                          <Cell key="free" fill="#dc2626" />
                          <Cell key="used" fill="#3B81F5" />
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-3 sm:gap-4 mt-2">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-primary-cyan" />
                      <span className="text-gray-300 text-[10px] sm:text-xs">
                        Free ({vm.freePercent.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-cyan-600" />
                      <span className="text-gray-300 text-[10px] sm:text-xs">
                        Used ({(100 - vm.freePercent).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-1/2">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div className="bg-gray-800/50 rounded-lg p-3 sm:p-4 text-center">
                      <p className="text-gray-400 text-[10px] sm:text-sm mb-1">Free Space</p>
                      <p className="text-xl sm:text-2xl font-bold text-primary-cyan">
                        {vm.freePercent.toFixed(1)}%
                      </p>
                      <p className="text-gray-400 text-[10px] sm:text-xs mt-1">
                        {vm.freeGB.toFixed(2)} GB
                      </p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3 sm:p-4 text-center">
                      <p className="text-gray-400 text-[10px] sm:text-sm mb-1">Used Space</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-400">
                        {(100 - vm.freePercent).toFixed(1)}%
                      </p>
                      <p className="text-gray-400 text-[10px] sm:text-xs mt-1">
                        {((vm.freeGB / vm.freePercent) * (100 - vm.freePercent)).toFixed(2)} GB
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {analysis && (
                <div className="mt-4 sm:mt-6 rounded-xl border border-primary-cyan/20 overflow-hidden">
                  <div className="px-3 sm:px-5 py-2 sm:py-3 border-b border-primary-cyan/20" style={{ backgroundColor: "#10182b" }}>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-primary-cyan/20 flex items-center justify-center">
                        <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-primary-cyan" />
                      </div>
                      <span className="text-primary-cyan text-xs sm:text-base font-bold tracking-wide">AI INSIGHTS</span>
                    </div>
                  </div>

                  <div className="p-3 sm:p-5" style={{ backgroundColor: "#10182b" }}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                      <div className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200" style={{ backgroundColor: "#1c2436" }}>
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                          </div>
                          <span className="text-primary-cyan text-[10px] sm:text-sm font-bold uppercase tracking-wide">Days Until Full</span>
                        </div>
                        <p className={`text-xl sm:text-3xl font-extrabold tracking-tight ${analysis.daysUntilFull && analysis.daysUntilFull <= 30 ? "text-red-500" : analysis.daysUntilFull && analysis.daysUntilFull <= 90 ? "text-orange-400" : "text-white"}`}>
                          {getDaysUntilFullDisplay(analysis.daysUntilFull)}
                        </p>
                        {analysis.riskLevel && (
                          <div className="mt-3 sm:mt-4">
                            <span className={`text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full font-semibold ${getRiskColor(analysis.riskLevel)}`}>
                              {analysis.riskLevel} Risk Level
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200" style={{ backgroundColor: "#1c2436" }}>
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                          </div>
                          <span className="text-primary-cyan text-[10px] sm:text-sm font-bold uppercase tracking-wide">Recommendation</span>
                        </div>
                        <p className="text-gray-200 text-xs sm:text-base leading-relaxed font-medium break-words">
                          {analysis.predictionRecommendation || "No recommendation available"}
                        </p>
                      </div>

                      <div className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200" style={{ backgroundColor: "#1c2436" }}>
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                          </div>
                          <span className="text-primary-cyan text-[10px] sm:text-sm font-bold uppercase tracking-wide">Optimization</span>
                        </div>
                        <p className="text-gray-200 text-xs sm:text-base leading-relaxed font-medium break-words">
                          {analysis.optimizationSuggestion || "No optimization suggestion available"}
                        </p>
                        {analysis.optimizationAction && analysis.optimizationAction !== "NONE" && (
                          <div className="mt-3 sm:mt-4">
                            <span className="text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full bg-primary-cyan/20 text-primary-cyan font-semibold">
                              Action: {analysis.optimizationAction.replace("_", " ")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DiskSpaceSummary;